import type en from "./en";
import type { Messages } from "./types";

export default {
  common: {
    loading: "Chargement…",
    save: "Enregistrer",
    saving: "Enregistrement…",
    cancel: "Annuler",
    confirm: "Confirmer",
    delete: "Supprimer",
    close: "Fermer",
    copy: "Copier",
    dash: "—",
    genericError: "Une erreur est survenue",
    confirmPasswordPlaceholder: "Confirmer le mot de passe",
    passwordMismatch: "Les mots de passe ne correspondent pas",
  },
  plan: {
    FREE: "Gratuit",
    PAID: "Payant",
  },
  nav: {
    console: "Console",
    home: "Accueil",
    apiKeys: "Clés API",
    subscription: "Abonnement",
    myAccount: "Mon compte",
    logout: "Se déconnecter",
    openMenu: "Ouvrir le menu",
  },
  auth: {
    tagline: "Espace comptes et clés API",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Mot de passe",
    passwordMinPlaceholder: "Mot de passe (8 caractères min.)",
    loginButton: "Se connecter",
    loggingIn: "Connexion…",
    noAccount: "Pas encore de compte ?",
    signUp: "S'inscrire",
    createAccount: "Créer un compte",
    creating: "Création…",
    alreadyAccount: "Déjà un compte ?",
    signIn: "Se connecter",
    forgotPasswordLink: "Mot de passe oublié ?",
  },
  passwordReset: {
    forgotTitle: "Mot de passe oublié",
    forgotDescription:
      "Entrez votre email, nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    emailSentTitle: "Email envoyé",
    emailSentDescription:
      "Si un compte existe pour cet email, vous recevrez un lien de réinitialisation d'ici quelques minutes.",
    submit: "Envoyer le lien",
    submitting: "Envoi…",
    backToLogin: "Retour à la connexion",
    resetTitle: "Réinitialiser le mot de passe",
    resetDescription: "Choisissez un nouveau mot de passe pour votre compte.",
    newPasswordPlaceholder: "Nouveau mot de passe (8 caractères min.)",
    resetSubmit: "Réinitialiser le mot de passe",
    resetSubmitting: "Réinitialisation…",
    resetSuccessTitle: "Mot de passe réinitialisé",
    resetSuccessDescription:
      "Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.",
    invalidTokenTitle: "Lien invalide",
    invalidTokenDescription: "Ce lien de réinitialisation est invalide ou a expiré.",
    requestNewLink: "Demander un nouveau lien",
  },
  account: {
    title: "Mon compte",
    subtitle: "Gérez votre profil, votre mot de passe et votre compte",
    profile: {
      title: "Profil",
      emailLabel: "Email",
      emailDescription: "Utilisé pour vous connecter à la console",
      planLabel: "Plan",
      planDescription: "Le forfait associé à votre compte",
    },
    security: {
      title: "Sécurité",
      currentPasswordPlaceholder: "Mot de passe actuel",
      newPasswordPlaceholder: "Nouveau mot de passe (8 caractères min.)",
      submit: "Changer le mot de passe",
      updating: "Mise à jour…",
    },
    danger: {
      title: "Zone dangereuse",
      deleteLabel: "Supprimer le compte",
      deleteDescription:
        "Cette action est irréversible et révoque immédiatement toutes vos clés API",
      passwordPlaceholder: "Mot de passe",
      deleteButton: "Supprimer",
      confirmTitle: "Supprimer définitivement le compte ?",
      confirmDescription:
        "Toutes vos clés API seront révoquées et cette action ne peut pas être annulée.",
    },
    toast: {
      emailUpdated: "Email mis à jour",
      passwordUpdated: "Mot de passe mis à jour",
    },
  },
  apiKeys: {
    title: "Clés API",
    subtitle:
      "Gérez les clés utilisées par vos instances auto-hébergées pour valider leur licence.",
    newKey: "Nouvelle clé",
    empty: "Aucune clé API pour le moment.",
    table: {
      key: "Clé",
      name: "Nom",
      created: "Créée le",
      lastUsed: "Dernière utilisation",
      status: "Statut",
    },
    unnamed: "Clé sans nom",
    revoked: "Révoquée",
    active: "Active",
    revokeAria: "Révoquer",
    confirmRevokeTitle: "Révoquer cette clé API ?",
    confirmRevokeDescription:
      "Toute instance auto-hébergée utilisant encore cette clé échouera immédiatement à sa vérification de licence. Cette action ne peut pas être annulée.",
    dialog: {
      createdTitle: "Clé API créée",
      createdDescription: "Copiez cette clé maintenant — elle ne sera plus jamais affichée.",
      copyAria: "Copier",
      createTitle: "Créer une clé API",
      createDescription: "Cette clé permettra à une instance auto-hébergée de valider sa licence.",
      namePlaceholder: "Nom (ex. Serveur de production)",
      generate: "Générer la clé",
      generating: "Création…",
    },
    toast: {
      revoked: "Clé révoquée",
    },
  },
  billing: {
    subtitle: "Gérez votre plan, votre abonnement et vos factures",
    yourPlan: "Votre plan",
    planDescription: {
      FREE: "Fonctionnalités de base, limites standard",
      PAID: "Accès complet à toutes les fonctionnalités",
    },
    yourSubscription: "Votre abonnement",
    subscriptionLabel: "Abonnement",
    noActiveSubscription: "Aucun abonnement actif",
    nextDueDate: "{{label}} · prochaine échéance le {{date}}",
    manage: "Gérer l'abonnement",
    redirecting: "Redirection…",
    selfHostedPremium: "Self-hosted Premium",
    cloud: "Cloud",
    resync: "Vérifier un paiement",
    resyncing: "Vérification…",
    resyncFound: "Paiement retrouvé, votre abonnement est activé.",
    resyncNotFound: "Aucun paiement retrouvé pour le moment.",
    status: {
      active: "Actif",
      trialing: "Période d'essai",
      past_due: "Paiement en retard",
      canceled: "Résilié",
      unpaid: "Impayé",
      incomplete: "Incomplet",
      incomplete_expired: "Expiré",
    },
    invoices: {
      title: "Factures",
      empty: "Aucune facture pour le moment",
      download: "Télécharger",
      status: {
        paid: "Payée",
        open: "En attente",
        void: "Annulée",
        uncollectible: "Impayée",
        draft: "Brouillon",
      },
    },
  },
  checkout: {
    cancel: {
      title: "Paiement annulé",
      description: "Aucun montant n'a été débité. Vous pouvez réessayer à tout moment.",
      backToAccount: "Retour à mon compte",
    },
    success: {
      notFoundTitle: "Session introuvable",
      notFoundDescription: "Nous n'avons pas pu vérifier ce paiement.",
      verifyingTitle: "Vérification du paiement…",
      verifyingDescription: "Un instant, merci de patienter.",
      confirmedTitle: "Paiement confirmé",
      activating: "Activation de votre abonnement en cours…",
      timeoutDescription:
        "L'activation prend plus de temps que prévu. Votre paiement a bien été reçu — l'activation devrait se terminer sous peu. Vous pouvez aussi actualiser cette page ou contacter le support.",
      refresh: "Actualiser",
      activatedTitle: "Abonnement activé",
      activatedSelfHosted: "Merci ! Créez une clé API pour activer votre instance self-hosted.",
      activatedCloud: "Merci ! Votre plan payant est actif.",
      createApiKey: "Créer une clé API",
      backToAccount: "Retour à mon compte",
    },
  },
  home: {
    title: "Accueil",
    welcome: "Bienvenue, {{email}}",
    planLabel: "Plan",
    activeApiKeys: "Clés API actives",
    manageApiKeys: "Gérer mes clés API",
    gettingStarted: {
      title: "Bien démarrer",
      description:
        "Créez une clé API pour connecter votre première instance auto-hébergée à ce serveur de licences.",
      action: "Créer ma première clé API",
    },
  },
  error: {
    boundary: "Une erreur inattendue est survenue.",
    backHome: "Retour à l'accueil",
  },
  errors: {
    STRIPE_ERROR:
      "Une erreur est survenue avec le prestataire de paiement. Réessayez ou contactez le support.",
    NO_STRIPE_CUSTOMER:
      "Aucun abonnement à gérer pour le moment — souscrivez d'abord un abonnement.",
    CHECKOUT_SESSION_NOT_FOUND: "Cette session de paiement est introuvable.",
    API_KEY_NOT_FOUND: "Cette clé API est introuvable.",
    INVALID_RESET_TOKEN: "Ce lien de réinitialisation est invalide ou a expiré.",
    ACCOUNT_ALREADY_EXISTS: "Un compte existe déjà avec cette adresse email.",
    INVALID_CREDENTIALS: "Email ou mot de passe invalide.",
  },
  settings: {
    appearance: {
      sectionTitle: "Apparence",
    },
    language: {
      label: "Langue de l'interface",
      description: "Choisissez la langue utilisée dans la console",
    },
    theme: {
      label: "Thème",
      description: "Choisissez le thème de couleur de la console",
      options: {
        light: "Clair",
        dark: "Sombre",
        system: "Système",
      },
    },
  },
} satisfies Messages<typeof en>;
